import React, { useRef } from "react";

/**
 * <ScrollableTabs> — the canonical tab component for the entire app.
 *
 * Mobile (default): tabs scroll horizontally within their container; the
 * page itself NEVER scrolls horizontally. Snap-to-start scrolling, touch-
 * friendly tap targets (>=44px), no visible scrollbar, soft fade hint
 * on overflow edge.
 *
 * Desktop: tabs render as a normal pill row. Same component, same API.
 *
 * Usage:
 *   <ScrollableTabs
 *     value={tab}
 *     onChange={setTab}
 *     tabs={[
 *       { id: "inquiries", label: "Inquiries" },
 *       { id: "promos",    label: "Promo codes" },
 *     ]}
 *     testidPrefix="admin-tab"
 *   />
 */
export function ScrollableTabs({ tabs, value, onChange, testidPrefix = "tab", className = "" }) {
  const listRef = useRef(null);

  // Auto-scroll the active tab into view (helps when programmatic changes
  // happen — e.g., onboarding step advance).
  React.useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-active="true"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [value]);

  return (
    <div
      role="tablist"
      className={`scrollable-tabs scrollable-tabs--fade ${className}`}
      data-testid={`${testidPrefix}-list`}
    >
      <div className="scrollable-tabs__inner" ref={listRef}>
        {tabs.map((t) => {
          const active = t.id === value;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-active={active}
              data-testid={`${testidPrefix}-${t.id}`}
              onClick={() => onChange(t.id)}
              className={`scrollable-tabs__tab px-4 py-2 text-sm font-medium rounded-full border-2 transition-all ${
                active
                  ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                  : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--ink-3)]"
              }`}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span
                  className={`ml-2 inline-flex items-center justify-center text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    active ? "bg-white/20 text-white" : "bg-[var(--ink-2)] text-white"
                  }`}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ScrollableTabs;
